// /src/components/tools/Carrousel.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Carousel } from 'react-responsive-carousel';
import Hls from 'hls.js';
import axios from 'axios';
import 'react-responsive-carousel/lib/styles/carousel.min.css';

const DEFAULT_CAROUSEL_ITEMS = [];

function normalizeCarouselItems(items) {
  if (!Array.isArray(items)) {
    return [];
  }

  return [...items]
    .sort((a, b) => Number(a.order || 0) - Number(b.order || 0))
    .map((item, index) => ({
      ...item,
      id: item.id || item.carouselId || `carousel-${index}`,
      duration: Number(item.duration || 6),
    }));
}

function isHlsItem(item) {
  const url = item?.url;
  if (!url) return false;

  const mediaType = String(item?.mediaType || '').toLowerCase();
  if (mediaType === 'video/hls' || mediaType === 'application/vnd.apple.mpegurl' || mediaType === 'application/x-mpegurl') {
    return true;
  }

  // Handle m3u8 urls with querystrings (e.g. ".../index.m3u8?token=...").
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.pathname.toLowerCase().endsWith('.m3u8');
  } catch {
    return String(url).toLowerCase().split('?')[0].endsWith('.m3u8');
  }
}

function isVideoItem(item) {
  // HLS playlists are commonly served as application/vnd.apple.mpegurl (not video/*).
  return item?.mediaType?.split('/')[0] === 'video' || isHlsItem(item);
}

function isImageItem(item) {
  return item?.mediaType?.split('/')[0] === 'image';
}

function shouldLoadMedia(index, activeIndex, totalItems) {
  if (totalItems <= 1) {
    return true;
  }

  const previousIndex = (activeIndex - 1 + totalItems) % totalItems;
  const nextIndex = (activeIndex + 1) % totalItems;

  return index === activeIndex || index === previousIndex || index === nextIndex;
}

function Carrousel() {
  const [carouselData, setCarouselData] = useState(() => normalizeCarouselItems(DEFAULT_CAROUSEL_ITEMS));
  const [activeIndex, setActiveIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  const videoRefs = useRef([]);
  const hlsRefs = useRef([]);
  const timerRef = useRef(null);

  const itemCount = carouselData.length;

  const activeItem = useMemo(() => {
    if (!itemCount) {
      return null;
    }

    return carouselData[activeIndex] || carouselData[0];
  }, [activeIndex, carouselData, itemCount]);

  useEffect(() => {
    let isMounted = true;

    const fetchCarouselData = async () => {
      try {
        const response = await axios.get('/api/public/carousel');
        const normalizedData = normalizeCarouselItems(response.data);

        if (!isMounted || normalizedData.length === 0) {
          return;
        }

        setCarouselData(normalizedData);
        setActiveIndex(0);
      } catch (error) {
        console.error('Error fetching carousel data:', error);
      }
    };

    fetchCarouselData();

    return () => {
      isMounted = false;
    };
  }, []);

  // (HLS init is handled via the <video> ref callback.)

  const ensureHlsForIndex = useCallback((index, videoEl) => {
    const item = carouselData[index];
    if (!videoEl || !item) return;

    const shouldLoad = shouldLoadMedia(index, activeIndex, itemCount);
    if (!shouldLoad || !isHlsItem(item)) return;

    // In React 18 StrictMode (dev), refs/effects can run twice; guard per element.
    if (videoEl.dataset?.carouselHlsInit === '1') return;
    if (hlsRefs.current[index]) return;
    if (videoEl.dataset) videoEl.dataset.carouselHlsInit = '1';

    const logPrefix = `[Carousel/HLS][${index}]`;
    const isDev = typeof process !== 'undefined' && process.env?.NODE_ENV !== 'production';

    if (videoEl.canPlayType('application/vnd.apple.mpegurl')) {
      if (isDev) console.log(logPrefix, 'NATIVE_HLS');
      videoEl.src = item.url;
      videoEl.addEventListener('canplay', () => {
        if (index === activeIndex) {
          videoEl.play().catch((error) => {
            console.error('Error playing native HLS carousel video:', error);
          });
        }
      }, { once: true });
      return;
    }

    if (!Hls.isSupported()) {
      if (isDev) console.warn(logPrefix, 'HLS_NOT_SUPPORTED');
      return;
    }

    const ua = typeof navigator !== 'undefined' ? String(navigator.userAgent || '') : '';
    const isFirefox = /firefox/i.test(ua);
    const isSafari = !/chrome|chromium|android/i.test(ua) && /safari/i.test(ua);
    const conservative = isFirefox || isSafari;

    const hls = new Hls({
      // Keep startup fast, then allow ABR to ramp up.
      startLevel: 0,
      autoStartLoad: true,

      // Avoid overly aggressive level choice on big screens.
      capLevelToPlayerSize: false,

      // Buffering knobs (more conservative on Firefox/Safari to reduce stalls).
      maxBufferHole: conservative ? 1.0 : 0.5,
      maxBufferLength: conservative ? 60 : 30,
      maxMaxBufferLength: conservative ? 120 : 60,

      // ABR: in conservative mode, ramp up slower to keep playback stable.
      abrEwmaDefaultEstimate: conservative ? 1_000_000 : 3_000_000,
      abrEwmaFastLive: conservative ? 5.0 : 3.0,
      abrEwmaSlowLive: conservative ? 12.0 : 9.0,
    });

    hls.on(Hls.Events.MEDIA_ATTACHED, () => {
      if (isDev) console.log(logPrefix, 'MEDIA_ATTACHED');
      hls.loadSource(item.url);
    });

    hls.on(Hls.Events.MANIFEST_PARSED, () => {
      if (isDev) console.log(logPrefix, 'MANIFEST_PARSED');
      if (index === activeIndex) {
        videoEl.muted = true;
        videoEl.play().catch((error) => {
          console.error('Error playing HLS carousel video:', error);
        });
      }
    });

    if (isDev) {
      hls.on(Hls.Events.LEVEL_LOADED, () => console.log(logPrefix, 'LEVEL_LOADED'));
      hls.on(Hls.Events.FRAG_LOADED, (_e, data) => console.log(logPrefix, 'FRAG_LOADED', data?.frag?.sn));
      videoEl.addEventListener('error', () => console.error(logPrefix, 'VIDEO_ERROR', videoEl.error));
      videoEl.addEventListener('stalled', () => console.warn(logPrefix, 'VIDEO_STALLED'));
      videoEl.addEventListener('waiting', () => console.warn(logPrefix, 'VIDEO_WAITING'));
      videoEl.addEventListener('playing', () => console.log(logPrefix, 'VIDEO_PLAYING'));
    }

    hls.on(Hls.Events.ERROR, (_event, data) => {
      console.error('HLS error:', data);
      if (!data?.fatal) return;

      switch (data.type) {
        case Hls.ErrorTypes.NETWORK_ERROR:
          hls.startLoad();
          break;
        case Hls.ErrorTypes.MEDIA_ERROR:
          hls.recoverMediaError();
          break;
        default:
          hls.destroy();
          hlsRefs.current[index] = null;
          break;
      }
    });

    // Non-fatal buffering stalls can happen on stricter browsers; recover by
    // capping to lowest level for a bit, restarting loads, and retrying play().
    hls.on(Hls.Events.ERROR, (_event, data) => {
      if (data?.details !== Hls.ErrorDetails.BUFFER_STALLED_ERROR) return;

      try {
        // Cap low to quickly refill buffer.
        hls.nextLevel = 0;
        hls.autoLevelCapping = 0;

        try {
          hls.startLoad();
        } catch (_) {
          // ignore
        }

        // If the element ended up paused after a stall, try again.
        if (videoEl.paused && index === activeIndex) {
          videoEl.play().catch(() => {});
        }

        // Release cap later so quality can ramp up again.
        setTimeout(() => {
          try {
            hls.autoLevelCapping = -1;
          } catch (_) {
            // ignore
          }
        }, 12000);
      } catch (_) {
        // ignore
      }
    });

    hls.attachMedia(videoEl);
    hlsRefs.current[index] = hls;
  }, [activeIndex, carouselData, itemCount]);

  useEffect(() => {
    return () => {
      hlsRefs.current.forEach((hls) => {
        if (hls) {
          hls.destroy();
        }
      });
  
      hlsRefs.current = [];
    };
  }, []);

  useEffect(() => {
    if (!activeItem || itemCount <= 1) {
      return undefined;
    }

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    const durationInMs = Math.max(Number(activeItem.duration || 6) * 1000 - 500, 1000);

    timerRef.current = setTimeout(() => {
      setActiveIndex((previousIndex) => (previousIndex + 1) % itemCount);
    }, durationInMs);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [activeItem, itemCount]);

  useEffect(() => {
    videoRefs.current.forEach((video, index) => {
      if (!video) {
        return;
      }

      if (index !== activeIndex) {
        video.pause();
        return;
      }

      const playNow = () => {
        // Avoid forcing a seek to 0 on every activation; it can trigger extra range requests.
        if (video.currentTime > 0.25) {
          video.currentTime = 0;
        }
        video.play().catch((error) => {
          console.error('Error playing carousel video:', error);
        });
      };

      if (video.readyState >= 2) {
        playNow();
        return;
      }

      // For HLS (and some browsers), readyState may still be < 2 when slide becomes active.
      // Ensure we attempt to play as soon as the element can play.
      video.addEventListener('canplay', playNow, { once: true });
      video.addEventListener('loadedmetadata', playNow, { once: true });
    });
  }, [activeIndex, carouselData]);

  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(window.scrollY <= 100);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleSlideChange = useCallback((index) => {
    setActiveIndex(index);
  }, []);

  const handleDotClick = useCallback((index) => {
    setActiveIndex(index);
  }, []);

  const renderMediaItem = useCallback((item, index) => {
    const shouldLoad = shouldLoadMedia(index, activeIndex, itemCount);
    const isActive = index === activeIndex;

    if (isImageItem(item)) {
      return (
        <img
          className="custom-carousel-image"
          src={shouldLoad ? item.url : undefined}
          alt={item.title || ''}
          loading={isActive ? 'eager' : 'lazy'}
          fetchpriority={isActive ? 'high' : 'low'}
        />
      );
    }

    if (isVideoItem(item)) {
      return (
        <video
          ref={(element) => {
            videoRefs.current[index] = element;
            if (element) {
              ensureHlsForIndex(index, element);
            }
          }}
          className="video-iframe"
          src={!isHlsItem(item) && shouldLoad ? item.url : undefined}
          autoPlay={isActive}
          muted
          loop
          playsInline
          preload={isActive ? 'auto' : 'metadata'}
        />
      );
    }

    return null;
  }, [activeIndex, ensureHlsForIndex, itemCount]);

  if (itemCount === 0) {
    return (
      <div className="carousel-with-title carousel-empty">
        <div
          data-resource="public.carousel"
          data-action="load"
          data-route="/api/public/carousel"
          className="display-none"
          data-description="Carousel"
        />
      </div>
    );
  }

  return (
    <div className="carousel-with-title">
      <div
        data-resource="public.carousel"
        data-action="load"
        data-route="/api/public/carousel"
        className="display-none"
        data-description="Carousel"
      />

      <Carousel
        selectedItem={activeIndex}
        onChange={handleSlideChange}
        autoPlay={false}
        infiniteLoop={false}
        useKeyboardArrows
        transitionTime={500}
        showThumbs={false}
        showStatus={false}
        showIndicators={false}
      >
        {carouselData.map((item, index) => (
          <div key={item.id} className="carousel-item">
            {renderMediaItem(item, index)}

            <div className="carousel-title-container">
              <div className="carousel-title">{item.title}</div>
              <div className="carousel-subtitle">{item.subtitle}</div>

              <ul className="carousel-custom-dots">
                {carouselData.map((dotItem, dotIndex) => (
                  <li
                    key={dotItem.id}
                    className={`dot ${dotIndex === activeIndex ? 'active' : ''}`}
                    role="button"
                    tabIndex={0}
                    aria-label={`Go to carousel item ${dotIndex + 1}`}
                    onClick={() => handleDotClick(dotIndex)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        handleDotClick(dotIndex);
                      }
                    }}
                  />
                ))}
              </ul>
            </div>
          </div>
        ))}
      </Carousel>

      <div className={`chevrons-container ${!isVisible ? 'fade-out' : ''}`}>
        <svg className="chevron" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
          <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6z" />
        </svg>
        <svg className="chevron" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
          <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6z" />
        </svg>
        <svg className="chevron" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
          <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6z" />
        </svg>
      </div>
    </div>
  );
}

export default Carrousel;